import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { Toaster } from "@/components/ui/toaster";
import { useToast } from "@/hooks/use-toast";

import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { FileDown, FileUp, KeyRound, LayoutDashboard, LogOut, Shield, Users, X, Loader2, Trash2, Link2, Copy, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api/v1`;
const APP_NAME = "buLockBox";
const BRAND_LOGO_URL = `${process.env.PUBLIC_URL || ""}/bu-creative-logo.png`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

function getCsrfFromCookie() {
  const m = document.cookie.match(/(?:^|; )pfs_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

api.interceptors.request.use((config) => {
  const csrf = getCsrfFromCookie();
  if (csrf && config.method && config.method.toUpperCase() !== "GET") {
    config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

function formatDateTimeShort(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  // Two-digit year, and no seconds, to keep the column narrow.
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/auth/me`);
      setUser(res.data.user);
      return res.data.user;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return { user, loading, refresh };
}

function Protected({ children, user, loading }) {
  const location = useLocation();
  if (loading) {
    return (
      <div data-testid="app-loading" className="p-8 text-sm text-muted-foreground">
        Caricamento…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

function SidebarLink({ icon, label, to, active, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-left border transition-colors ${
        active
          ? "bg-[radial-gradient(1200px_circle_at_0%_0%,rgba(99,102,241,0.18),transparent_45%)] border-white/10 text-foreground"
          : "bg-transparent border-transparent text-muted-foreground hover:text-foreground hover:bg-white/5"
      }`}
    >
      <span className="inline-flex items-center justify-center size-9 rounded-lg bg-white/5 border border-white/10">
        {icon}
      </span>
      <span className="flex-1">{label}</span>
      <span className="text-[11px] text-muted-foreground">/</span>
    </button>
  );
}

function BrandLogo({ onClick, sizeClass = "h-11", className = "" }) {
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      data-testid={onClick ? "brand-logo-button" : "brand-logo"}
      onClick={onClick}
      {...(onClick ? { type: "button" } : {})}
      className={`inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white/5 px-3 py-2 transition-transform duration-200 ease-out ${
        onClick ? "hover:scale-[1.02] hover:opacity-90" : ""
      } ${className}`}
    >
      <img
        data-testid="brand-logo-image"
        src={BRAND_LOGO_URL}
        alt="BU Creative"
        className={`${sizeClass} w-auto object-contain`}
      />
    </Wrapper>
  );
}

function AppShell({ user, onLogout, children }) {
  const nav = useNavigate();
  const location = useLocation();
  const isSuperAdmin = user?.role === "super_admin";
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isViewer = user?.role === "viewer";

  const links = [
    {
      key: "files",
      label: "Files",
      path: "/files",
      icon: <FileDown className="size-4" />,
      show: true,
      testid: "sidebar-files-link",
    },
    {
      key: "stats",
      label: "Stats",
      path: "/stats",
      icon: <LayoutDashboard className="size-4" />,
      show: isAdmin,
      testid: "sidebar-stats-link",
    },
    {
      key: "tenants",
      label: "Tenants & API Keys",
      path: "/tenants",
      icon: <KeyRound className="size-4" />,
      show: isAdmin,
      testid: "sidebar-tenants-link",
    },
    {
      key: "users",
      label: "Users & Roles",
      path: "/users",
      icon: <Users className="size-4" />,
      show: isAdmin,
      testid: "sidebar-users-link",
    },
    {
      key: "audit",
      label: "Audit Log",
      path: "/audit",
      icon: <Shield className="size-4" />,
      show: isAdmin,
      testid: "sidebar-audit-link",
    },
  ].filter((l) => l.show);

  return (
    <div data-testid="app-shell" className="min-h-screen bg-[#070A12] text-white">
      <div className="pointer-events-none fixed inset-0 opacity-60">
        <div className="absolute -top-24 left-[-10%] h-[420px] w-[520px] rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute top-24 right-[-10%] h-[360px] w-[480px] rounded-full bg-fuchsia-500/10 blur-3xl" />
      </div>

      <div className="relative z-10 grid grid-cols-12 gap-0">
        <aside className="col-span-12 lg:col-span-3 xl:col-span-2 p-4 lg:p-6 border-b lg:border-b-0 lg:border-r border-white/10">
          <div className="flex flex-col items-center gap-2 text-center">
            <BrandLogo onClick={() => nav("/files")} sizeClass="h-10 lg:h-12" className="mx-auto" />
            <div className="space-y-1">
              <div data-testid="brand-title" className="text-lg font-semibold tracking-tight">
                {APP_NAME}
              </div>
              <div data-testid="brand-subtitle" className="text-xs text-white/60">
                Private File Server — Admin
              </div>
            </div>
            <Badge data-testid="user-role-badge" variant="secondary" className="bg-white/10 text-white border-white/10">
              {user?.role || "viewer"}
            </Badge>
          </div>

          <Separator className="my-4 bg-white/10" />

          <div className="space-y-2">
            {links.map((l) => (
              <SidebarLink
                key={l.key}
                to={l.path}
                label={l.label}
                icon={l.icon}
                active={location.pathname.startsWith(l.path)}
                onClick={() => nav(l.path)}
                testid={l.testid}
              />
            ))}
          </div>

          <Separator className="my-4 bg-white/10" />

          <div className="space-y-2">
            <div data-testid="sidebar-user-email" className="text-xs text-white/60">
              {user?.email}
            </div>
            <Button
              data-testid="sidebar-logout-button"
              onClick={onLogout}
              variant="secondary"
              className="w-full rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
            >
              <LogOut className="size-4 mr-2" />
              Logout
            </Button>
          </div>
        </aside>

        <main className="col-span-12 lg:col-span-9 xl:col-span-10 p-4 lg:p-8">
          <div className="max-w-[1200px]">
            {children}
            <div className="mt-10 text-xs text-white/40">
              <div data-testid="footer-note">
                Operazioni amministrative tracciate su Audit Log. Configura correttamente CORS e cookie secure in produzione.
              </div>
            </div>
          </div>
        </main>
      </div>

      <Toaster />
    </div>
  );
}

function LoginPage({ onLogin }) {
  const { toast } = useToast();
  const nav = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/files";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/auth/login`, { email, password });
      const u = await onLogin();
      if (!u) {
        toast({
          title: "Sessione non salvata",
          description:
            "Il server accetta la login, ma il browser non sta inviando i cookie di sessione (probabile blocco cookie / third-party). Prova in incognito o abilita i cookie per questo sito.",
          variant: "destructive",
        });
        return;
      }
      nav(from);
    } catch (err) {
      toast({
        title: "Login fallito",
        description: err?.response?.data?.detail || "Credenziali non valide",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-[#070A12] text-white">
      <div className="pointer-events-none fixed inset-0 opacity-70">
        <div className="absolute -top-20 left-[-15%] h-[420px] w-[620px] rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-10%] h-[420px] w-[520px] rounded-full bg-violet-500/10 blur-3xl" />
      </div>

      <div className="relative z-10 grid grid-cols-12">
        <div className="col-span-12 lg:col-span-5 p-6 lg:p-12 border-b lg:border-b-0 lg:border-r border-white/10">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <BrandLogo sizeClass="h-10 sm:h-11" className="rounded-xl" />
              <div data-testid="login-brand" className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">
                {APP_NAME}
              </div>
            </div>
            <div data-testid="login-subtitle" className="text-base md:text-lg text-white/70 max-w-md">
              Dashboard amministrativa per upload/download sicuro, multi-tenant e audit trail.
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7 p-6 lg:p-12">
          <div className="max-w-lg">
            <Card data-testid="login-card" className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle data-testid="login-title">Login</CardTitle>
                <CardDescription data-testid="login-description" className="text-white/60">
                  Sessione con cookie httpOnly e CSRF.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handle} className="space-y-4">
                  <div className="space-y-2">
                    <Label data-testid="login-email-label" htmlFor="email" className="text-white/80">
                      Email
                    </Label>
                    <Input
                      data-testid="login-email-input"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="nome@azienda.com"
                      className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label data-testid="login-password-label" htmlFor="password" className="text-white/80">
                      Password
                    </Label>
                    <Input
                      data-testid="login-password-input"
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                    />
                  </div>

                  <Button
                    data-testid="login-submit-button"
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-xl bg-indigo-400/20 hover:bg-indigo-400/30 border border-indigo-300/30 text-white"
                  >
                    {loading ? "Accesso…" : "Entra"}
                  </Button>

                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Toaster />
    </div>
  );
}

function FilesPage({ isAdmin, isSuperAdmin, canUpload }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [mime, setMime] = useState("all");
  const [showDeleted, setShowDeleted] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [tenantFilter, setTenantFilter] = useState("all");
  const [uploaderFilter, setUploaderFilter] = useState("all");
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);

  // Fetch tenants and users for filters
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [tRes, uRes] = await Promise.all([
          api.get("/tenants"),
          api.get("/users"),
        ]);
        setTenants(tRes.data || []);
        setUsers(uRes.data || []);
      } catch (_) {}
    };
    if (isAdmin) fetchFilters();
  }, [isAdmin]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (mime !== "all") params.mime = mime;
      if (showDeleted) params.include_deleted = true;
      if (tenantFilter !== "all") params.tenant_id = tenantFilter;
      if (uploaderFilter !== "all") params.uploaded_by = uploaderFilter;
      const res = await api.get(`/files`, { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile caricare i file",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      const next = searchInput.trim();
      if (next !== q) setQ(next);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput, q]);

  useEffect(() => {
    fetchFiles();
  }, [q, mime, showDeleted, tenantFilter, uploaderFilter]);

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const next = searchInput.trim();
      if (next !== q) {
        setQ(next);
      } else {
        fetchFiles();
      }
    }
  };

  const clearSearch = () => {
    setSearchInput("");
    setQ("");
  };

  const onUploadChunked = async (file) => {
    // super_admin has no own tenant: a target tenant must be selected.
    if (isSuperAdmin && tenantFilter === "all") {
      toast({
        title: "Seleziona un tenant",
        description: "Scegli un tenant dal filtro prima di caricare un file.",
        variant: "destructive",
      });
      return;
    }

    setUploadProgress({ phase: "init", percent: 0, uploadId: null });

    try {
      // 1) initiate
      const init = await api.post(`/files/initiate-multipart`, {
        filename: file.name,
        mime: file.type || "application/octet-stream",
        size: file.size,
        tags: [],
        ...(isSuperAdmin ? { tenant_id: tenantFilter } : {}),
      });

      const { upload_id: uploadId, chunk_size: chunkSize, total_chunks: totalChunks } = init.data;
      setUploadProgress({ phase: "uploading", percent: 0, uploadId });

      // 2) read already uploaded parts (resume)
      const st = await api.get(`/files/multipart/${uploadId}`);
      const received = new Set(st.data.received_parts || []);

      // 3) upload missing parts
      let uploadedParts = received.size;
      const updatePercent = () => {
        const pct = Math.round((uploadedParts / totalChunks) * 100);
        setUploadProgress({ phase: "uploading", percent: pct, uploadId });
      };

      updatePercent();

      for (let part = 1; part <= totalChunks; part += 1) {
        if (received.has(part)) continue;

        const start = (part - 1) * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const blob = file.slice(start, end);

        const fd = new FormData();
        fd.append("chunk", blob, `${file.name}.part${part}`);

        await api.put(`/files/multipart/${uploadId}/parts/${part}`, fd);

        uploadedParts += 1;
        updatePercent();
      }

      // 4) complete
      setUploadProgress({ phase: "finalizing", percent: 100, uploadId });
      await api.post(`/files/multipart/${uploadId}/complete`);

      setUploadProgress({ phase: "done", percent: 100, uploadId });
      toast({ title: "Upload completato", description: file.name });
      fetchFiles();

      // clear progress after a moment
      setTimeout(() => setUploadProgress(null), 1200);
    } catch (err) {
      setUploadProgress(null);
      toast({
        title: "Upload fallito",
        description: err?.response?.data?.detail?.message || err?.response?.data?.detail || "Errore upload",
        variant: "destructive",
      });
    }
  };

  const onDownload = async (id, originalName) => {
    try {
      const res = await api.get(`/files/${id}/download`, { params: { expires_in: 3600 } });
      // Token endpoint now requires auth (session cookie), so we can download directly in this tab.
      const url = `${BACKEND_URL}${res.data.download_url}`;

      const a = document.createElement("a");
      a.href = url;
      a.download = originalName || "file";
      a.rel = "noopener";
      a.target = "_self";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast({
        title: "Download fallito",
        description: err?.response?.data?.detail || "Errore download",
        variant: "destructive",
      });
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/files/${id}`);
      toast({ title: "Cancellato", description: `File ${id}` });
      fetchFiles();
    } catch (err) {
      toast({
        title: "Delete fallito",
        description: err?.response?.data?.detail || "Errore delete",
        variant: "destructive",
      });
    }
  };

  const [shareDialog, setShareDialog] = useState(null); // { fileId, fileName }
  const [shareExpiry, setShareExpiry] = useState("3600");
  const [shareMaxDl, setShareMaxDl] = useState("1");
  const [shareResult, setShareResult] = useState(null); // { url, expires_at, max_downloads }
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const onShare = async () => {
    setShareLoading(true);
    try {
      const res = await api.post(`/files/${shareDialog.fileId}/share`, {
        expires_in: parseInt(shareExpiry, 10),
        max_downloads: parseInt(shareMaxDl, 10),
      });
      // share_url is a path; resolve it against the API origin. Concatenating
      // window.location.origin and BACKEND_URL produced a doubled host once
      // BACKEND_URL became absolute.
      const fullUrl = new URL(res.data.share_url, BACKEND_URL || window.location.origin).toString();
      setShareResult({ url: fullUrl, expires_at: res.data.expires_at, max_downloads: res.data.max_downloads });
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile generare il link",
        variant: "destructive",
      });
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareUrl = async () => {
    if (!shareResult?.url) return;
    try {
      await navigator.clipboard.writeText(shareResult.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Errore", description: "Impossibile copiare", variant: "destructive" });
    }
  };

  return (
    <div data-testid="files-page" className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div data-testid="files-title" className="text-2xl font-semibold tracking-tight">
              Files
            </div>
            <div data-testid="files-subtitle" className="text-sm text-white/60">
              Totale: <span className="text-white/80">{total}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="files-refresh-button"
              onClick={fetchFiles}
              variant="secondary"
              disabled={loading}
              className="h-10 rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10 disabled:opacity-60"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" />
                  Aggiorna
                </span>
              ) : (
                "Aggiorna"
              )}
            </Button>
            {canUpload && (
              <label
                data-testid="files-upload-label"
                className="inline-flex h-10 items-center justify-center rounded-xl bg-indigo-400/20 hover:bg-indigo-400/30 border border-indigo-300/30 px-4 text-sm cursor-pointer transition-colors"
              >
                <FileUp className="size-4 mr-2" />
                Carica file
                <input
                  data-testid="files-upload-input"
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onUploadChunked(f);
                    e.target.value = "";
                  }}
                />
              </label>
            )}
          </div>
        </div>

        <div data-testid="files-toolbar" className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5 min-w-[200px] flex-1 max-w-[320px]">
            <Label data-testid="files-search-label" className="text-xs text-white/50">Cerca</Label>
            <div className="relative">
              <Input
                data-testid="files-search-input"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Nome file…"
                className="h-9 bg-black/30 border-white/10 text-white text-sm placeholder:text-white/30 rounded-lg pr-8"
              />
              {searchInput && (
                <button
                  data-testid="files-search-clear-button"
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="space-y-1.5 min-w-[150px]">
            <Label data-testid="files-mime-label" className="text-xs text-white/50">MIME</Label>
            <Select value={mime} onValueChange={setMime}>
              <SelectTrigger data-testid="files-mime-select" className="h-9 rounded-lg bg-black/30 border-white/10 text-white text-sm">
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent className="bg-[#0B1020] border-white/10 text-white">
                <SelectItem data-testid="files-mime-option-all" value="all">Tutti</SelectItem>
                <SelectItem data-testid="files-mime-option-pdf" value="application/pdf">application/pdf</SelectItem>
                <SelectItem data-testid="files-mime-option-png" value="image/png">image/png</SelectItem>
                <SelectItem data-testid="files-mime-option-jpeg" value="image/jpeg">image/jpeg</SelectItem>
                <SelectItem data-testid="files-mime-option-octet" value="application/octet-stream">application/octet-stream</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isAdmin && tenants.length > 0 && (
            <div className="space-y-1.5 min-w-[150px]">
              <Label data-testid="files-tenant-filter-label" className="text-xs text-white/50">Tenant</Label>
              <Select value={tenantFilter} onValueChange={setTenantFilter}>
                <SelectTrigger data-testid="files-tenant-filter-select" className="h-9 rounded-lg bg-black/30 border-white/10 text-white text-sm">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B1020] border-white/10 text-white">
                  <SelectItem value="all">Tutti</SelectItem>
                  {tenants.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {isAdmin && users.length > 0 && (
            <div className="space-y-1.5 min-w-[150px]">
              <Label data-testid="files-uploader-filter-label" className="text-xs text-white/50">Caricato da</Label>
              <Select value={uploaderFilter} onValueChange={setUploaderFilter}>
                <SelectTrigger data-testid="files-uploader-filter-select" className="h-9 rounded-lg bg-black/30 border-white/10 text-white text-sm">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B1020] border-white/10 text-white">
                  <SelectItem value="all">Tutti</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label data-testid="files-show-deleted-label" className="text-xs text-white/50">Cancellati</Label>
            <div className="flex items-center gap-2 h-9 rounded-lg border border-white/10 bg-black/20 px-3">
              <Checkbox
                data-testid="files-show-deleted-checkbox"
                checked={showDeleted}
                onCheckedChange={(v) => setShowDeleted(Boolean(v))}
                className="border-white/30 data-[state=checked]:bg-indigo-400/50 data-[state=checked]:border-indigo-200/40"
              />
              <div className="text-sm text-white/70">Mostra</div>
            </div>
          </div>
        </div>
      </div>



      {uploadProgress && (
        <Card data-testid="chunk-upload-progress-card" className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle data-testid="chunk-upload-progress-title" className="text-base">
              Upload in corso
            </CardTitle>
            <CardDescription data-testid="chunk-upload-progress-desc" className="text-white/60">
              Upload ID: <span className="text-white/80">{uploadProgress.uploadId}</span> — fase: {uploadProgress.phase}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-4">
              <div data-testid="chunk-upload-progress-bar" className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
                <div
                  data-testid="chunk-upload-progress-bar-fill"
                  className="h-full bg-indigo-400/40"
                  style={{ width: `${uploadProgress.percent}%` }}
                />
              </div>
              <div data-testid="chunk-upload-progress-percent" className="text-sm text-white/70 w-14 text-right">
                {uploadProgress.percent}%
              </div>
            </div>
            <div data-testid="chunk-upload-progress-hint" className="mt-2 text-xs text-white/40">
              Se chiudi la pagina, puoi riprendere: l’upload mantiene lo stato lato server finché non scade.
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle data-testid="files-table-title" className="text-base">
            Elenco
          </CardTitle>
          <CardDescription data-testid="files-table-description" className="text-white/60">
            Download via URL firmata a scadenza.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea type="auto" className="w-full pb-2">
            <div className="min-w-[900px]">
            <Table data-testid="files-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-white/60">Nome</TableHead>
                  <TableHead className="text-white/60">Tenant</TableHead>
                  <TableHead className="text-white/60">Caricato da</TableHead>
                  <TableHead className="text-white/60 w-[110px]">MIME</TableHead>
                  <TableHead className="text-white/60 text-right">Size</TableHead>
                  <TableHead className="text-white/60">Status</TableHead>
                  <TableHead className="text-white/60">Caricato il</TableHead>
                  <TableHead className="text-white/60">Ultimo download</TableHead>
                  <TableHead className="text-white/60">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell data-testid="files-loading" colSpan={9} className="text-white/60">
                      Caricamento…
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell data-testid="files-empty" colSpan={9} className="text-white/60">
                      Nessun file.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((f) => (
                    <TableRow key={f.id}>
                      <TableCell className="max-w-[220px]">
                        <div data-testid={`file-name-${f.id}`} className="font-medium text-white/90 truncate" title={f.original_name}>
                          {f.original_name}
                        </div>
                        <div data-testid={`file-id-${f.id}`} className="text-xs text-white/40 truncate" title={f.id}>
                          {f.id}
                        </div>
                      </TableCell>
                      <TableCell data-testid={`file-tenant-${f.id}`} className="text-sm text-white/70 max-w-[150px] truncate" title={f.tenant_name || f.tenant_id}>
                        {f.tenant_name || f.tenant_id}
                      </TableCell>
                      <TableCell data-testid={`file-uploader-${f.id}`} className="text-sm text-white/70 max-w-[180px]">
                        <div className="truncate" title={f.uploaded_by_name || "—"}>
                          {f.uploaded_by_name || "—"}
                        </div>
                        {f.uploaded_by_type && (
                          <div className="text-xs text-white/40">{f.uploaded_by_type === "api_key" ? "API Key" : "Utente"}</div>
                        )}
                      </TableCell>
                      <TableCell data-testid={`file-mime-${f.id}`} className="text-sm text-white/70 w-[110px] max-w-[110px] truncate" title={f.mime}>
                        {f.mime}
                      </TableCell>
                      <TableCell data-testid={`file-size-${f.id}`} className="text-sm text-white/70 text-right whitespace-nowrap">
                        {formatBytes(f.size)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          data-testid={`file-status-${f.id}`}
                          className={`border ${f.status === "active" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-200" : "bg-white/10 border-white/10 text-white"}`}
                        >
                          {f.status}
                        </Badge>
                      </TableCell>
                      <TableCell data-testid={`file-created-at-${f.id}`} className="text-sm text-white/70 whitespace-nowrap">
                        {formatDateTimeShort(f.created_at)}
                      </TableCell>
                      <TableCell>
                        {f.last_download_at ? (
                          <div className="space-y-1">
                            <div data-testid={`file-last-download-at-${f.id}`} className="text-sm text-white/80 whitespace-nowrap">
                              {formatDateTimeShort(f.last_download_at)}
                            </div>
                            <div data-testid={`file-last-download-ip-${f.id}`} className="text-xs text-white/40">
                              IP: {f.last_download_ip || "—"}
                            </div>
                          </div>
                        ) : (
                          <div data-testid={`file-last-download-empty-${f.id}`} className="text-sm text-white/40">
                            —
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            data-testid={`file-download-button-${f.id}`}
                            onClick={() => onDownload(f.id, f.original_name)}
                            size="icon"
                            title="Download"
                            className="size-8 rounded-lg bg-white/10 hover:bg-white/15 text-white border border-white/10"
                          >
                            <FileDown className="size-4" />
                          </Button>

                          {isAdmin && (
                            <Button
                              data-testid={`file-share-button-${f.id}`}
                              onClick={() => { setShareDialog({ fileId: f.id, fileName: f.original_name }); setShareResult(null); setShareExpiry("3600"); setShareMaxDl("1"); setCopied(false); }}
                              size="icon"
                              title="Genera URL download"
                              className="size-8 rounded-lg bg-white/10 hover:bg-white/15 text-white border border-white/10"
                            >
                              <Link2 className="size-4" />
                            </Button>
                          )}

                          {isAdmin && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  data-testid={`file-delete-open-button-${f.id}`}
                                  size="icon"
                                  title="Elimina"
                                  className="size-8 rounded-lg bg-white/10 hover:bg-red-500/20 text-white border border-white/10"
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent data-testid={`file-delete-dialog-${f.id}`} className="bg-[#0B1020] border-white/10 text-white">
                                <DialogHeader>
                                  <DialogTitle data-testid={`file-delete-title-${f.id}`}>Conferma delete</DialogTitle>
                                  <DialogDescription data-testid={`file-delete-desc-${f.id}`} className="text-white/60">
                                    Questa azione rimuove il file dallo storage locale e marca il metadata come deleted.
                                  </DialogDescription>
                                </DialogHeader>
                                <DialogFooter>
                                  <Button
                                    data-testid={`file-delete-confirm-button-${f.id}`}
                                    onClick={() => onDelete(f.id)}
                                    className="rounded-xl bg-fuchsia-400/20 hover:bg-fuchsia-400/30 border border-fuchsia-300/30 text-white"
                                  >
                                    Cancella
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Share link dialog */}
      <Dialog open={!!shareDialog} onOpenChange={(open) => { if (!open) setShareDialog(null); }}>
        <DialogContent className="bg-[#0B1020] border-white/10 text-white sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Genera URL di download</DialogTitle>
            <DialogDescription className="text-white/60">
              {shareDialog?.fileName}
            </DialogDescription>
          </DialogHeader>

          {!shareResult ? (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-white/70">Validità del link</Label>
                <Select value={shareExpiry} onValueChange={setShareExpiry}>
                  <SelectTrigger className="h-10 rounded-xl bg-black/30 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B1020] border-white/10 text-white">
                    <SelectItem value="300">5 minuti</SelectItem>
                    <SelectItem value="900">15 minuti</SelectItem>
                    <SelectItem value="1800">30 minuti</SelectItem>
                    <SelectItem value="3600">1 ora</SelectItem>
                    <SelectItem value="14400">4 ore</SelectItem>
                    <SelectItem value="43200">12 ore</SelectItem>
                    <SelectItem value="86400">1 giorno</SelectItem>
                    <SelectItem value="259200">3 giorni</SelectItem>
                    <SelectItem value="604800">7 giorni</SelectItem>
                    <SelectItem value="2592000">30 giorni</SelectItem>
                    <SelectItem value="7776000">90 giorni</SelectItem>
                    <SelectItem value="15552000">180 giorni</SelectItem>
                    <SelectItem value="31536000">365 giorni</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Numero massimo di download</Label>
                <Input
                  type="number"
                  min="1"
                  max="1000000"
                  value={shareMaxDl}
                  onChange={(e) => setShareMaxDl(e.target.value)}
                  className="h-10 bg-black/30 border-white/10 text-white rounded-xl"
                />
              </div>
              <DialogFooter>
                <Button
                  onClick={onShare}
                  disabled={shareLoading}
                  className="rounded-xl bg-indigo-400/20 hover:bg-indigo-400/30 border border-indigo-300/30 text-white"
                >
                  {shareLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : <Link2 className="size-4 mr-2" />}
                  Genera link
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-white/70">URL di download</Label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={shareResult.url}
                    className="h-10 bg-black/30 border-white/10 text-white rounded-xl text-sm"
                  />
                  <Button
                    onClick={copyShareUrl}
                    size="icon"
                    className="size-10 shrink-0 rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
                    title="Copia"
                  >
                    {copied ? <Check className="size-4 text-emerald-400" /> : <Copy className="size-4" />}
                  </Button>
                </div>
              </div>
              <div className="text-sm text-white/50 space-y-1">
                <div>Scadenza: <span className="text-white/80">{new Date(shareResult.expires_at).toLocaleString()}</span></div>
                <div>Download massimi: <span className="text-white/80">{shareResult.max_downloads}</span></div>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => setShareDialog(null)}
                  className="rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
                >
                  Chiudi
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TenantsKeysPage({ isAdmin, isSuperAdmin }) {
  const { toast } = useToast();
  const [tenants, setTenants] = useState([]);
  const [keys, setKeys] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [newTenantName, setNewTenantName] = useState("");
  const [newTenantNotes, setNewTenantNotes] = useState("");
  const [uploadExpMode, setUploadExpMode] = useState("expiring"); // expiring|noexp
  const [uploadExpValue, setUploadExpValue] = useState(60);
  const [uploadExpUnit, setUploadExpUnit] = useState("minutes");

  const [newKeyTenant, setNewKeyTenant] = useState("");
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyRole, setNewKeyRole] = useState("service");
  const [createdKey, setCreatedKey] = useState(null);

  const [editTenantOpen, setEditTenantOpen] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [editName, setEditName] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editExpMode, setEditExpMode] = useState("expiring");
  const [editExpValue, setEditExpValue] = useState(60);
  const [editExpUnit, setEditExpUnit] = useState("minutes");

  const [deleteTenant, setDeleteTenant] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  const expUnitLabel = (unit) => {
    if (unit === "hours") return "ore";
    if (unit === "days") return "giorni";
    return "minuti";
  };

  const policyLabel = (tenant) => {
    if (tenant?.upload_link_no_expiration) return "Senza scadenza";
    if (!tenant?.upload_link_expiration_value) return "—";
    return `${tenant.upload_link_expiration_value} ${expUnitLabel(tenant.upload_link_expiration_unit)}`;
  };

  const refresh = async () => {
    setLoadingTenants(true);
    try {
      const t = await api.get(`/tenants`);
      setTenants(t.data);
      const k = await api.get(`/api-keys`);
      setKeys(k.data);
      if (!newKeyTenant && t.data[0]?.id) setNewKeyTenant(t.data[0].id);
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile caricare dati",
        variant: "destructive",
      });
    } finally {
      setLoadingTenants(false);
    }
  };

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin]);

  const createTenant = async () => {
    if (!isSuperAdmin) return;
    try {
      const payload = {
        name: newTenantName,
        notes: newTenantNotes || null,
        upload_link_no_expiration: uploadExpMode === "noexp",
        upload_link_expiration_value: uploadExpMode === "expiring" ? Number(uploadExpValue) : null,
        upload_link_expiration_unit: uploadExpMode === "expiring" ? uploadExpUnit : null,
      };

      await api.post(`/tenants`, payload);
      toast({ title: "Tenant creato" });
      setNewTenantName("");
      setNewTenantNotes("");
      refresh();
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile creare tenant",
        variant: "destructive",
      });
    }
  };

  const openEditTenant = (tenant) => {
    setEditTenant(tenant);
    setEditName(tenant?.name || "");
    setEditNotes(tenant?.notes || "");
    setEditExpMode(tenant?.upload_link_no_expiration ? "noexp" : "expiring");
    setEditExpValue(tenant?.upload_link_expiration_value || 60);
    setEditExpUnit(tenant?.upload_link_expiration_unit || "minutes");
    setEditTenantOpen(true);
  };

  const closeEditTenant = () => {
    setEditTenantOpen(false);
    setEditTenant(null);
  };

  const saveEditTenant = async () => {
    if (!editTenant) return;
    try {
      const payload = {
        name: editName,
        notes: editNotes || null,
        upload_link_no_expiration: editExpMode === "noexp",
        upload_link_expiration_value: editExpMode === "expiring" ? Number(editExpValue) : null,
        upload_link_expiration_unit: editExpMode === "expiring" ? editExpUnit : null,
      };

      await api.put(`/tenants/${editTenant.id}`, payload);
      toast({ title: "Tenant aggiornato" });
      closeEditTenant();
      refresh();
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail?.message || err?.response?.data?.detail || "Impossibile aggiornare tenant",
        variant: "destructive",
      });
    }
  };

  const createKey = async () => {
    try {
      const res = await api.post(`/api-keys`, { tenant_id: newKeyTenant, name: newKeyName, role: newKeyRole, allowed_ips: [] });
      setCreatedKey(res.data);
      toast({ title: "API key creata", description: "Il secret è mostrato una sola volta." });
      setNewKeyName("");
      refresh();
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile creare API key",
        variant: "destructive",
      });
    }
  };

  const revokeKey = async (id) => {
    try {
      await api.post(`/api-keys/${id}/revoke`);
      toast({ title: "Revocata" });
      refresh();
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile revocare",
        variant: "destructive",
      });
    }
  };

  const rotateKey = async (id) => {
    try {
      const res = await api.post(`/api-keys/${id}/rotate`);
      setCreatedKey(res.data);
      toast({ title: "Ruotata", description: "Nuovo secret mostrato una sola volta." });
      refresh();
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile ruotare",
        variant: "destructive",
      });
    }
  };

  const handleDeleteTenant = async () => {
    if (!deleteTenant) return;
    try {
      await api.delete(`/tenants/${deleteTenant.id}`);
      toast({ title: "Tenant eliminato" });
      setDeleteTenant(null);
      setDeleteConfirm("");
      refresh();
    } catch (err) {
      const blocking = err?.response?.data?.detail?.blocking;
      if (blocking) {
        const reasons = [];
        if (blocking.files) reasons.push(`Ci sono ${blocking.files} file`);
        if (blocking.api_keys) reasons.push(`Ci sono ${blocking.api_keys} API key`);
        if (blocking.users) reasons.push(`Ci sono ${blocking.users} utenti`);
        if (blocking.pending_uploads) reasons.push(`Ci sono ${blocking.pending_uploads} upload pendenti`);
        toast({
          title: "Eliminazione bloccata",
          description: reasons.join(" · ") || "Vincoli non soddisfatti",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Errore",
          description: err?.response?.data?.detail || "Impossibile eliminare tenant",
          variant: "destructive",
        });
      }
    }
  };

  if (!isAdmin) {
    return (
      <Card data-testid="tenants-not-admin" className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle>Permesso negato</CardTitle>
          <CardDescription className="text-white/60">Solo admin/super_admin può gestire i tenant.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div data-testid="tenants-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div data-testid="tenants-title" className="text-2xl font-semibold tracking-tight">
            Tenants & API Keys
          </div>
          <div data-testid="tenants-subtitle" className="text-sm text-white/60">
            Namespace isolati: ogni API key è legata a un tenant.
          </div>
        </div>

        <Button
          data-testid="tenants-refresh-button"
          onClick={refresh}
          variant="secondary"
          className="rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
        >
          Aggiorna
        </Button>
      </div>

      <div className={`grid grid-cols-1 ${isSuperAdmin ? "xl:grid-cols-2" : ""} gap-4`}>
        {isSuperAdmin && (
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle data-testid="create-tenant-title" className="text-base">Crea tenant</CardTitle>
              <CardDescription data-testid="create-tenant-desc" className="text-white/60">Usato per segregare file e policy.</CardDescription>

              <div className="space-y-2">
                <Label data-testid="tenant-upload-expiration-label" className="text-white/70">
                  Upload Link Expiration
                </Label>
                <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                  <RadioGroup value={uploadExpMode} onValueChange={setUploadExpMode} className="gap-3">
                    <div className="flex items-start gap-3">
                      <RadioGroupItem data-testid="tenant-upload-expiring-radio" value="expiring" className="mt-1 border-white/30 text-white" />
                      <div className="flex-1">
                        <div data-testid="tenant-upload-expiring-text" className="text-sm text-white/80">
                          Con scadenza
                        </div>
                        {uploadExpMode === "expiring" && (
                          <div className="mt-2 flex flex-col sm:flex-row gap-2">
                            <Input
                              data-testid="tenant-upload-expiration-value-input"
                              type="number"
                              min={1}
                              value={uploadExpValue}
                              onChange={(e) => setUploadExpValue(e.target.value)}
                              className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl sm:w-32"
                            />
                            <Select value={uploadExpUnit} onValueChange={setUploadExpUnit}>
                              <SelectTrigger
                                data-testid="tenant-upload-expiration-unit-select"
                                className="rounded-xl bg-black/30 border-white/10 text-white sm:w-40"
                              >
                                <SelectValue placeholder="minuti" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#0B1020] border-white/10 text-white">
                                <SelectItem data-testid="tenant-upload-expiration-unit-minutes" value="minutes">
                                  minuti
                                </SelectItem>
                                <SelectItem data-testid="tenant-upload-expiration-unit-hours" value="hours">
                                  ore
                                </SelectItem>
                                <SelectItem data-testid="tenant-upload-expiration-unit-days" value="days">
                                  giorni
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <RadioGroupItem data-testid="tenant-upload-noexp-radio" value="noexp" className="mt-1 border-white/30 text-white" />
                      <div className="flex-1">
                        <div data-testid="tenant-upload-noexp-text" className="text-sm text-white/80">
                          Senza scadenza
                        </div>
                        <div data-testid="tenant-upload-noexp-hint" className="text-xs text-white/40 mt-1">
                          Il link di upload resterà valido finché non viene completato o pulito dal housekeeping.
                        </div>
                      </div>
                    </div>
                  </RadioGroup>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label data-testid="tenant-name-label" className="text-white/70">Nome</Label>
                <Input
                  data-testid="tenant-name-input"
                  value={newTenantName}
                  onChange={(e) => setNewTenantName(e.target.value)}
                  placeholder="Acme Srl"
                  className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label data-testid="tenant-notes-label" className="text-white/70">Note</Label>
                <Textarea
                  data-testid="tenant-notes-input"
                  value={newTenantNotes}
                  onChange={(e) => setNewTenantNotes(e.target.value)}
                  placeholder="Descrizione tenant (opzionale)"
                  className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl min-h-[90px]"
                />
              </div>
              <Button
                data-testid="tenant-create-button"
                onClick={createTenant}
                disabled={!newTenantName.trim()}
                className="rounded-xl bg-indigo-400/20 hover:bg-indigo-400/30 border border-indigo-300/30 text-white"
              >
                Crea
              </Button>

              <Separator className="my-2 bg-white/10" />

              <div className="text-xs text-white/60" data-testid="tenant-count">
                Totale tenants: <span className="text-white/80">{tenants.length}</span>
              </div>
              <div data-testid="tenant-policy-note" className="text-xs text-white/40">
                La policy vale per i nuovi link generati (non retroattiva).
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle data-testid="create-apikey-title" className="text-base">Genera API key</CardTitle>
            <CardDescription data-testid="create-apikey-desc" className="text-white/60">
              Mostriamo il secret una sola volta.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label data-testid="apikey-tenant-label" className="text-white/70">Tenant</Label>
                <Select value={newKeyTenant} onValueChange={setNewKeyTenant} disabled={!isSuperAdmin}>
                  <SelectTrigger data-testid="apikey-tenant-select" className="rounded-xl bg-black/30 border-white/10 text-white">
                    <SelectValue placeholder="Seleziona tenant" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B1020] border-white/10 text-white">
                    {tenants.map((t) => (
                      <SelectItem data-testid={`apikey-tenant-option-${t.id}`} key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label data-testid="apikey-role-label" className="text-white/70">Ruolo</Label>
                <Select value={newKeyRole} onValueChange={setNewKeyRole}>
                  <SelectTrigger data-testid="apikey-role-select" className="rounded-xl bg-black/30 border-white/10 text-white">
                    <SelectValue placeholder="service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B1020] border-white/10 text-white">
                    <SelectItem data-testid="apikey-role-option-service" value="service">service</SelectItem>
                    <SelectItem data-testid="apikey-role-option-viewer" value="viewer">viewer</SelectItem>
                    <SelectItem data-testid="apikey-role-option-admin" value="admin">admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label data-testid="apikey-name-label" className="text-white/70">Nome</Label>
              <Input
                data-testid="apikey-name-input"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="ThirdApp-prod"
                className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl"
              />
            </div>

            <Button
              data-testid="apikey-create-button"
              onClick={createKey}
              disabled={!newKeyTenant || !newKeyName.trim()}
              className="rounded-xl bg-indigo-400/20 hover:bg-indigo-400/30 border border-indigo-300/30 text-white"
            >
              Genera
            </Button>

            {createdKey && (
              <Card data-testid="apikey-created-card" className="bg-black/30 border-white/10">
                <CardHeader>
                  <CardTitle data-testid="apikey-created-title" className="text-sm">
                    Secret (solo una volta)
                  </CardTitle>
                  <CardDescription data-testid="apikey-created-desc" className="text-white/60">
                    Salvalo ora: non sarà più recuperabile.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div data-testid="apikey-created-id" className="text-xs text-white/60">
                    API Key ID: <span className="text-white/80">{createdKey.api_key_id}</span>
                  </div>
                  <div data-testid="apikey-created-secret" className="text-xs text-white/60 break-all">
                    Secret: <span className="text-white/80">{createdKey.api_key_secret}</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle data-testid="tenants-list-title" className="text-base">Elenco tenant</CardTitle>
          <CardDescription data-testid="tenants-list-desc" className="text-white/60">
            Modifica nome, note e policy. Admin può gestire solo il proprio tenant.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table data-testid="tenants-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-white/60">Nome</TableHead>
                <TableHead className="text-white/60">Note</TableHead>
                <TableHead className="text-white/60">Policy Upload</TableHead>
                <TableHead className="text-white/60">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingTenants ? (
                <TableRow>
                  <TableCell data-testid="tenants-loading" colSpan={4} className="text-white/60">
                    Caricamento…
                  </TableCell>
                </TableRow>
              ) : tenants.length === 0 ? (
                <TableRow>
                  <TableCell data-testid="tenants-empty" colSpan={4} className="text-white/60">
                    Nessun tenant.
                  </TableCell>
                </TableRow>
              ) : (
                tenants.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell data-testid={`tenant-name-${t.id}`} className="text-white/80">
                      {t.name}
                    </TableCell>
                    <TableCell data-testid={`tenant-notes-${t.id}`} className="text-white/60">
                      {t.notes || "—"}
                    </TableCell>
                    <TableCell data-testid={`tenant-policy-${t.id}`} className="text-white/60">
                      {policyLabel(t)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          data-testid={`tenant-edit-button-${t.id}`}
                          size="sm"
                          onClick={() => openEditTenant(t)}
                          className="rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
                        >
                          Modifica
                        </Button>
                        {isSuperAdmin && (
                          <Button
                            data-testid={`tenant-delete-button-${t.id}`}
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setDeleteTenant(t);
                              setDeleteConfirm("");
                            }}
                            className="rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
                          >
                            Elimina
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle data-testid="apikey-list-title" className="text-base">API Keys</CardTitle>
          <CardDescription data-testid="apikey-list-desc" className="text-white/60">
            Il secret non è visibile. Puoi revocare o ruotare.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table data-testid="apikey-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-white/60">ID</TableHead>
                <TableHead className="text-white/60">Tenant</TableHead>
                <TableHead className="text-white/60">Nome</TableHead>
                <TableHead className="text-white/60">Role</TableHead>
                <TableHead className="text-white/60">Active</TableHead>
                <TableHead className="text-white/60">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.length === 0 ? (
                <TableRow>
                  <TableCell data-testid="apikey-empty" colSpan={6} className="text-white/60">
                    Nessuna chiave.
                  </TableCell>
                </TableRow>
              ) : (
                keys.map((k) => (
                  <TableRow key={k.id}>
                    <TableCell data-testid={`apikey-id-${k.id}`} className="text-white/80">
                      {k.id}
                    </TableCell>
                    <TableCell data-testid={`apikey-tenant-${k.id}`} className="text-white/60">
                      {k.tenant_id}
                    </TableCell>
                    <TableCell data-testid={`apikey-name-${k.id}`} className="text-white/60">
                      {k.name}
                    </TableCell>
                    <TableCell>
                      <Badge data-testid={`apikey-role-${k.id}`} className="bg-white/10 border-white/10 text-white">
                        {k.role}
                      </Badge>
                    </TableCell>
                    <TableCell data-testid={`apikey-active-${k.id}`} className="text-white/60">
                      {k.active ? "yes" : "no"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          data-testid={`apikey-rotate-button-${k.id}`}
                          size="sm"
                          onClick={() => rotateKey(k.id)}
                          className="rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
                        >
                          Rotate
                        </Button>
                        <Button
                          data-testid={`apikey-revoke-button-${k.id}`}
                          size="sm"
                          onClick={() => revokeKey(k.id)}
                          variant="secondary"
                          className="rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
                        >
                          Revoke
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={editTenantOpen} onOpenChange={(open) => (!open ? closeEditTenant() : null)}>
        <DialogContent data-testid="tenant-edit-dialog" className="bg-[#0B1020] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle data-testid="tenant-edit-title">Modifica tenant</DialogTitle>
            <DialogDescription data-testid="tenant-edit-desc" className="text-white/60">
              Aggiorna nome, note e policy. Le modifiche valgono solo per i nuovi link.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label data-testid="tenant-edit-name-label" className="text-white/70">Nome</Label>
              <Input
                data-testid="tenant-edit-name-input"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label data-testid="tenant-edit-notes-label" className="text-white/70">Note</Label>
              <Textarea
                data-testid="tenant-edit-notes-input"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl min-h-[90px]"
              />
            </div>
            <div className="space-y-2">
              <Label data-testid="tenant-edit-expiration-label" className="text-white/70">Upload Link Expiration</Label>
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <RadioGroup value={editExpMode} onValueChange={setEditExpMode} className="gap-3">
                  <div className="flex items-start gap-3">
                    <RadioGroupItem data-testid="tenant-edit-expiring-radio" value="expiring" className="mt-1 border-white/30 text-white" />
                    <div className="flex-1">
                      <div data-testid="tenant-edit-expiring-text" className="text-sm text-white/80">Con scadenza</div>
                      {editExpMode === "expiring" && (
                        <div className="mt-2 flex flex-col sm:flex-row gap-2">
                          <Input
                            data-testid="tenant-edit-expiration-value-input"
                            type="number"
                            min={1}
                            value={editExpValue}
                            onChange={(e) => setEditExpValue(e.target.value)}
                            className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl sm:w-32"
                          />
                          <Select value={editExpUnit} onValueChange={setEditExpUnit}>
                            <SelectTrigger
                              data-testid="tenant-edit-expiration-unit-select"
                              className="rounded-xl bg-black/30 border-white/10 text-white sm:w-40"
                            >
                              <SelectValue placeholder="minuti" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0B1020] border-white/10 text-white">
                              <SelectItem data-testid="tenant-edit-expiration-unit-minutes" value="minutes">minuti</SelectItem>
                              <SelectItem data-testid="tenant-edit-expiration-unit-hours" value="hours">ore</SelectItem>
                              <SelectItem data-testid="tenant-edit-expiration-unit-days" value="days">giorni</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <RadioGroupItem data-testid="tenant-edit-noexp-radio" value="noexp" className="mt-1 border-white/30 text-white" />
                    <div className="flex-1">
                      <div data-testid="tenant-edit-noexp-text" className="text-sm text-white/80">Senza scadenza</div>
                    </div>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              data-testid="tenant-edit-cancel-button"
              onClick={closeEditTenant}
              variant="secondary"
              className="rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
            >
              Annulla
            </Button>
            <Button
              data-testid="tenant-edit-save-button"
              onClick={saveEditTenant}
              className="rounded-xl bg-indigo-400/20 hover:bg-indigo-400/30 border border-indigo-300/30 text-white"
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTenant)} onOpenChange={(open) => (!open ? setDeleteTenant(null) : null)}>
        <AlertDialogContent data-testid="tenant-delete-dialog" className="bg-[#0B1020] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="tenant-delete-title">Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription data-testid="tenant-delete-desc" className="text-white/60">
              Scrivi DELETE per confermare l'eliminazione soft del tenant. L'azione verrà completata solo se i vincoli sono soddisfatti.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label data-testid="tenant-delete-confirm-label" className="text-white/70">Conferma</Label>
            <Input
              data-testid="tenant-delete-confirm-input"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="DELETE"
              className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tenant-delete-cancel-button" className="bg-white/10 text-white border border-white/10">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="tenant-delete-confirm-button"
              onClick={handleDeleteTenant}
              disabled={deleteConfirm !== "DELETE"}
              className="bg-fuchsia-400/20 text-white border border-fuchsia-300/30"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
function UsersPage({ user }) {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("viewer");
  const [tenantId, setTenantId] = useState("");
  const [tenants, setTenants] = useState([]);
  const [expiresOn, setExpiresOn] = useState("");

  const [filterEmail, setFilterEmail] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterTenant, setFilterTenant] = useState("all");

  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState("viewer");
  const [editTenantId, setEditTenantId] = useState("");
  const [editExpiresOn, setEditExpiresOn] = useState("");
  const [resetPassword, setResetPassword] = useState("");

  const [deleteUser, setDeleteUser] = useState(null);

  const isSuperAdmin = user?.role === "super_admin";
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const formatExpiry = (value) => {
    if (!value) return "—";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return "—";
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yyyy = dt.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const refresh = async () => {
    setLoadingUsers(true);
    try {
      const params = {};
      if (filterEmail) params.q = filterEmail;
      if (filterRole !== "all") params.role = filterRole;
      if (filterStatus !== "all") params.status = filterStatus;
      if (isSuperAdmin && filterTenant !== "all") params.tenant_id = filterTenant;
      const res = await api.get(`/users`, { params });
      setUsers(res.data);
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile caricare utenti",
        variant: "destructive",
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const res = await api.get(`/tenants`);
      setTenants(res.data);
      if (!tenantId && res.data[0]?.id) setTenantId(res.data[0].id);
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile caricare tenant",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (isSuperAdmin) fetchTenants();
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin, filterEmail, filterRole, filterStatus, filterTenant, isSuperAdmin]);

  const createUser = async () => {
    try {
      const payload = { email, password, role };
      if (expiresOn) payload.expires_on = expiresOn;

      // tenant assignment rules
      if (role === "super_admin") {
        payload.tenant_id = null;
      } else if (isSuperAdmin) {
        payload.tenant_id = tenantId;
      } else {
        payload.tenant_id = user?.tenant_id;
      }

      await api.post(`/users`, payload);
      toast({ title: "Utente creato" });
      setEmail("");
      setPassword("");
      setRole("viewer");
      setExpiresOn("");
      refresh();
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile creare",
        variant: "destructive",
      });
    }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditEmail(u.email || "");
    setEditRole(u.role || "viewer");
    setEditTenantId(u.tenant_id || "");
    setEditExpiresOn(u.expires_at ? formatExpiry(u.expires_at) : "");
    setResetPassword("");
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditUser(null);
    setResetPassword("");
  };

  const saveEdit = async () => {
    if (!editUser) return;
    try {
      const payload = {};
      const currentExpiry = editUser.expires_at ? formatExpiry(editUser.expires_at) : "";

      if (editEmail && editEmail !== editUser.email) payload.email = editEmail;
      if (editRole && editRole !== editUser.role) payload.role = editRole;

      if (isSuperAdmin) {
        if (editRole !== "super_admin") {
          payload.tenant_id = editTenantId || null;
        }
      }

      if (editExpiresOn !== currentExpiry) {
        payload.expires_on = editExpiresOn || "";
      }

      if (Object.keys(payload).length === 0) {
        closeEdit();
        return;
      }

      await api.put(`/users/${editUser.id}`, payload);
      toast({ title: "Utente aggiornato" });
      closeEdit();
      refresh();
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile aggiornare",
        variant: "destructive",
      });
    }
  };

  const handleToggle = async (u, nextValue) => {
    try {
      if (nextValue) {
        await api.patch(`/users/${u.id}/enable`);
      } else {
        await api.patch(`/users/${u.id}/disable`);
      }
      refresh();
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile aggiornare stato",
        variant: "destructive",
      });
    }
  };

  const handleResetPassword = async () => {
    if (!editUser || !resetPassword) return;
    try {
      await api.post(`/users/${editUser.id}/reset-password`, { password: resetPassword });
      toast({ title: "Password resettata" });
      setResetPassword("");
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Reset password fallito",
        variant: "destructive",
      });
    }
  };

  const handleForceLogout = async () => {
    if (!editUser) return;
    try {
      await api.post(`/users/${editUser.id}/force-logout`);
      toast({ title: "Logout forzato" });
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Force logout fallito",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    try {
      await api.delete(`/users/${deleteUser.id}`);
      toast({ title: "Utente eliminato" });
      setDeleteUser(null);
      refresh();
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile eliminare utente",
        variant: "destructive",
      });
    }
  };

  const canCreate = Boolean(email.trim() && password && (role === "super_admin" || !isSuperAdmin || tenantId));

  if (!isAdmin) {
    return (
      <Card data-testid="users-not-admin" className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle>Permesso negato</CardTitle>
          <CardDescription className="text-white/60">Solo admin/super_admin può gestire gli utenti.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div data-testid="users-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div data-testid="users-title" className="text-2xl font-semibold tracking-tight">
            Users & Roles
          </div>
          <div data-testid="users-subtitle" className="text-sm text-white/60">
            Gestione utenti per la dashboard.
          </div>
        </div>
        <Button
          data-testid="users-refresh-button"
          onClick={refresh}
          variant="secondary"
          className="rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
        >
          Aggiorna
        </Button>
      </div>

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle data-testid="users-create-title" className="text-base">Crea utente</CardTitle>
          <CardDescription data-testid="users-create-desc" className="text-white/60">Password hashata con bcrypt.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="space-y-2">
            <Label data-testid="users-email-label" className="text-white/70">Email</Label>
            <Input
              data-testid="users-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl"
              placeholder="nome@azienda.com"
            />
          </div>
          <div className="space-y-2">
            <Label data-testid="users-password-label" className="text-white/70">Password</Label>
            <Input
              data-testid="users-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl"
              placeholder="••••••••"
            />
          </div>
          <div className="space-y-2">
            <Label data-testid="users-role-label" className="text-white/70">Ruolo</Label>
            <Select
              value={role}
              onValueChange={(value) => {
                setRole(value);
                if (value === "super_admin") setTenantId("");
              }}
            >
              <SelectTrigger data-testid="users-role-select" className="rounded-xl bg-black/30 border-white/10 text-white">
                <SelectValue placeholder="viewer" />
              </SelectTrigger>
              <SelectContent className="bg-[#0B1020] border-white/10 text-white">
                <SelectItem data-testid="users-role-option-viewer" value="viewer">viewer</SelectItem>
                <SelectItem data-testid="users-role-option-user" value="user">user</SelectItem>
                <SelectItem data-testid="users-role-option-admin" value="admin">admin</SelectItem>
                {isSuperAdmin && (
                  <SelectItem data-testid="users-role-option-super-admin" value="super_admin">
                    super_admin
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {isSuperAdmin ? (
            <div className="space-y-2">
              <Label data-testid="users-tenant-label" className="text-white/70">
                Tenant
              </Label>
              <Select
                value={tenantId}
                onValueChange={setTenantId}
                disabled={role === "super_admin"}
              >
                <SelectTrigger
                  data-testid="users-tenant-select"
                  className="rounded-xl bg-black/30 border-white/10 text-white"
                >
                  <SelectValue placeholder="Seleziona tenant" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B1020] border-white/10 text-white">
                  {tenants.map((t) => (
                    <SelectItem data-testid={`users-tenant-option-${t.id}`} key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {role === "super_admin" && (
                <div data-testid="users-tenant-hidden-hint" className="text-xs text-white/40">
                  Tenant non richiesto per super_admin.
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Label data-testid="users-tenant-label" className="text-white/70">
                Tenant
              </Label>
              <Input
                data-testid="users-tenant-readonly-input"
                value={user?.tenant_id || ""}
                readOnly
                className="bg-black/30 border-white/10 text-white/80 rounded-xl"
                placeholder="(assegnato automaticamente)"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label data-testid="users-expires-label" className="text-white/70">Scadenza (DD-MM-YYYY)</Label>
            <Input
              data-testid="users-expires-input"
              value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)}
              className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl"
              placeholder="31-12-2026"
            />
          </div>

          <div className="md:col-span-5">
            <Button
              data-testid="users-create-button"
              onClick={createUser}
              disabled={!canCreate}
              className="rounded-xl bg-indigo-400/20 hover:bg-indigo-400/30 border border-indigo-300/30 text-white"
            >
              Crea
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle data-testid="users-filters-title" className="text-base">Filtri</CardTitle>
          <CardDescription data-testid="users-filters-desc" className="text-white/60">Ricerca per email, ruolo e stato.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="space-y-2">
            <Label data-testid="users-filter-email-label" className="text-white/70">Email</Label>
            <Input
              data-testid="users-filter-email-input"
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
              className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl"
              placeholder="cerca per email"
            />
          </div>
          <div className="space-y-2">
            <Label data-testid="users-filter-role-label" className="text-white/70">Ruolo</Label>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger data-testid="users-filter-role-select" className="rounded-xl bg-black/30 border-white/10 text-white">
                <SelectValue placeholder="tutti" />
              </SelectTrigger>
              <SelectContent className="bg-[#0B1020] border-white/10 text-white">
                <SelectItem data-testid="users-filter-role-all" value="all">tutti</SelectItem>
                <SelectItem data-testid="users-filter-role-viewer" value="viewer">viewer</SelectItem>
                <SelectItem data-testid="users-filter-role-user" value="user">user</SelectItem>
                <SelectItem data-testid="users-filter-role-admin" value="admin">admin</SelectItem>
                <SelectItem data-testid="users-filter-role-superadmin" value="super_admin">super_admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label data-testid="users-filter-status-label" className="text-white/70">Stato</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="users-filter-status-select" className="rounded-xl bg-black/30 border-white/10 text-white">
                <SelectValue placeholder="tutti" />
              </SelectTrigger>
              <SelectContent className="bg-[#0B1020] border-white/10 text-white">
                <SelectItem data-testid="users-filter-status-all" value="all">tutti</SelectItem>
                <SelectItem data-testid="users-filter-status-active" value="active">attivi</SelectItem>
                <SelectItem data-testid="users-filter-status-disabled" value="disabled">disabilitati</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isSuperAdmin && (
            <div className="space-y-2">
              <Label data-testid="users-filter-tenant-label" className="text-white/70">Tenant</Label>
              <Select value={filterTenant} onValueChange={setFilterTenant}>
                <SelectTrigger data-testid="users-filter-tenant-select" className="rounded-xl bg-black/30 border-white/10 text-white">
                  <SelectValue placeholder="tutti" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B1020] border-white/10 text-white">
                  <SelectItem data-testid="users-filter-tenant-all" value="all">tutti</SelectItem>
                  {tenants.map((t) => (
                    <SelectItem data-testid={`users-filter-tenant-${t.id}`} key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle data-testid="users-list-title" className="text-base">Elenco utenti</CardTitle>
        </CardHeader>
        <CardContent>
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-white/60">Email</TableHead>
                <TableHead className="text-white/60">Role</TableHead>
                <TableHead className="text-white/60">Tenant</TableHead>
                <TableHead className="text-white/60">Stato</TableHead>
                <TableHead className="text-white/60">Scadenza</TableHead>
                <TableHead className="text-white/60">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingUsers ? (
                <TableRow>
                  <TableCell data-testid="users-loading" colSpan={6} className="text-white/60">
                    Caricamento…
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell data-testid="users-empty" colSpan={6} className="text-white/60">
                    Nessun utente.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell data-testid={`users-email-${u.id}`} className="text-white/80">
                      {u.email}
                    </TableCell>
                    <TableCell data-testid={`users-role-${u.id}`} className="text-white/60">
                      {u.role}
                    </TableCell>
                    <TableCell data-testid={`users-tenant-${u.id}`} className="text-white/60">
                      {u.tenant_id || "—"}
                    </TableCell>
                    <TableCell data-testid={`users-status-${u.id}`} className="text-white/60">
                      <div className="flex items-center gap-3">
                        <Switch
                          data-testid={`users-status-toggle-${u.id}`}
                          checked={!u.disabled}
                          onCheckedChange={(v) => handleToggle(u, v)}
                        />
                        <Badge
                          data-testid={`users-status-badge-${u.id}`}
                          className={`border ${u.disabled ? "bg-white/10 border-white/10 text-white" : "bg-emerald-500/10 border-emerald-500/20 text-emerald-200"}`}
                        >
                          {u.disabled ? "disabled" : "active"}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell data-testid={`users-expiry-${u.id}`} className="text-white/60">
                      {formatExpiry(u.expires_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          data-testid={`users-edit-button-${u.id}`}
                          size="sm"
                          onClick={() => openEdit(u)}
                          className="rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
                        >
                          Modifica
                        </Button>
                        <Button
                          data-testid={`users-delete-button-${u.id}`}
                          size="sm"
                          onClick={() => setDeleteUser(u)}
                          disabled={u.id === user?.id}
                          variant="secondary"
                          className="rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
                        >
                          Elimina
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={editOpen} onOpenChange={(open) => (!open ? closeEdit() : null)}>
        <DialogContent data-testid="user-edit-dialog" className="bg-[#0B1020] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle data-testid="user-edit-title">Modifica utente</DialogTitle>
            <DialogDescription data-testid="user-edit-desc" className="text-white/60">
              Aggiorna email, ruolo, tenant, scadenza e sicurezza.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label data-testid="user-edit-email-label" className="text-white/70">Email</Label>
              <Input
                data-testid="user-edit-email-input"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label data-testid="user-edit-role-label" className="text-white/70">Ruolo</Label>
              <Select
                value={editRole}
                onValueChange={(value) => {
                  setEditRole(value);
                  if (value === "super_admin") setEditTenantId("");
                }}
              >
                <SelectTrigger data-testid="user-edit-role-select" className="rounded-xl bg-black/30 border-white/10 text-white">
                  <SelectValue placeholder="viewer" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B1020] border-white/10 text-white">
                  <SelectItem data-testid="user-edit-role-option-viewer" value="viewer">viewer</SelectItem>
                  <SelectItem data-testid="user-edit-role-option-user" value="user">user</SelectItem>
                  <SelectItem data-testid="user-edit-role-option-admin" value="admin">admin</SelectItem>
                  {isSuperAdmin && (
                    <SelectItem data-testid="user-edit-role-option-superadmin" value="super_admin">super_admin</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            {isSuperAdmin ? (
              <div className="space-y-2 md:col-span-2">
                <Label data-testid="user-edit-tenant-label" className="text-white/70">Tenant</Label>
                <Select
                  value={editTenantId}
                  onValueChange={setEditTenantId}
                  disabled={editRole === "super_admin"}
                >
                  <SelectTrigger data-testid="user-edit-tenant-select" className="rounded-xl bg-black/30 border-white/10 text-white">
                    <SelectValue placeholder="Seleziona tenant" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B1020] border-white/10 text-white">
                    {tenants.map((t) => (
                      <SelectItem data-testid={`user-edit-tenant-option-${t.id}`} key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editRole === "super_admin" && (
                  <div data-testid="user-edit-tenant-hint" className="text-xs text-white/40">
                    Tenant non richiesto per super_admin.
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2 md:col-span-2">
                <Label data-testid="user-edit-tenant-label" className="text-white/70">Tenant</Label>
                <Input
                  data-testid="user-edit-tenant-readonly"
                  value={editUser?.tenant_id || ""}
                  readOnly
                  className="bg-black/30 border-white/10 text-white/80 rounded-xl"
                />
              </div>
            )}
            <div className="space-y-2 md:col-span-2">
              <Label data-testid="user-edit-expires-label" className="text-white/70">Scadenza (DD-MM-YYYY)</Label>
              <Input
                data-testid="user-edit-expires-input"
                value={editExpiresOn}
                onChange={(e) => setEditExpiresOn(e.target.value)}
                className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                placeholder="31-12-2026"
              />
            </div>
          </div>

          <Separator className="my-2 bg-white/10" />

          <div className="space-y-2">
            <Label data-testid="user-reset-password-label" className="text-white/70">Reset password</Label>
            <div className="flex flex-col md:flex-row gap-2">
              <Input
                data-testid="user-reset-password-input"
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                className="bg-black/30 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                placeholder="Nuova password"
              />
              <Button
                data-testid="user-reset-password-button"
                onClick={handleResetPassword}
                disabled={!resetPassword}
                className="rounded-xl bg-fuchsia-400/20 hover:bg-fuchsia-400/30 border border-fuchsia-300/30 text-white"
              >
                Reset
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <Button
              data-testid="user-force-logout-button"
              onClick={handleForceLogout}
              variant="secondary"
              className="rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
            >
              Force logout
            </Button>
            <DialogFooter className="gap-2">
              <Button
                data-testid="user-edit-cancel-button"
                onClick={closeEdit}
                variant="secondary"
                className="rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
              >
                Annulla
              </Button>
              <Button
                data-testid="user-edit-save-button"
                onClick={saveEdit}
                className="rounded-xl bg-indigo-400/20 hover:bg-indigo-400/30 border border-indigo-300/30 text-white"
              >
                Salva
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteUser)} onOpenChange={(open) => (!open ? setDeleteUser(null) : null)}>
        <AlertDialogContent data-testid="user-delete-dialog" className="bg-[#0B1020] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="user-delete-title">Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription data-testid="user-delete-desc" className="text-white/60">
              Eliminare definitivamente l'utente selezionato. Questa azione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel-button" className="bg-white/10 text-white border border-white/10">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="user-delete-confirm-button"
              onClick={handleDelete}
              className="bg-fuchsia-400/20 text-white border border-fuchsia-300/30"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AuditPage({ isAdmin }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);

  const refresh = async () => {
    try {
      const res = await api.get(`/audit`, { params: { limit: 200 } });
      setItems(res.data);
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile caricare audit",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <Card data-testid="audit-not-admin" className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle>Permesso negato</CardTitle>
          <CardDescription className="text-white/60">Solo admin/super_admin può vedere laudit log.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div data-testid="audit-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div data-testid="audit-title" className="text-2xl font-semibold tracking-tight">
            Audit Log
          </div>
          <div data-testid="audit-subtitle" className="text-sm text-white/60">
            Traccia append-only delle azioni.
          </div>
        </div>
        <Button
          data-testid="audit-refresh-button"
          onClick={refresh}
          variant="secondary"
          className="rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
        >
          Aggiorna
        </Button>
      </div>

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle data-testid="audit-table-title" className="text-base">Eventi</CardTitle>
          <CardDescription data-testid="audit-table-desc" className="text-white/60">
            Filtri avanzati e export CSV: step successivo.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[560px]">
            <Table data-testid="audit-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-white/60">Timestamp</TableHead>
                  <TableHead className="text-white/60">Action</TableHead>
                  <TableHead className="text-white/60">Result</TableHead>
                  <TableHead className="text-white/60">Actor</TableHead>
                  <TableHead className="text-white/60">Tenant</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell data-testid="audit-empty" colSpan={5} className="text-white/60">
                      Nessun evento.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell data-testid={`audit-ts-${a.id}`} className="text-white/70">
                        {new Date(a.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell data-testid={`audit-action-${a.id}`} className="text-white/80">
                        {a.action}
                      </TableCell>
                      <TableCell>
                        <Badge
                          data-testid={`audit-result-${a.id}`}
                          className={`border ${a.result === "ok" ? "bg-indigo-400/15 border-indigo-300/20 text-indigo-100" : "bg-white/10 border-white/10 text-white"}`}
                        >
                          {a.result}
                        </Badge>
                      </TableCell>
                      <TableCell data-testid={`audit-actor-${a.id}`} className="text-white/60">
                        {a.actor_type}:{a.actor_id}
                      </TableCell>
                      <TableCell data-testid={`audit-tenant-${a.id}`} className="text-white/60">
                        {a.tenant_id || "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}

function StatsPage({ isAdmin }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [errorsOpen, setErrorsOpen] = useState(false);
  const [errorsHours, setErrorsHours] = useState(24);
  const [errorsData, setErrorsData] = useState(null);
  const [errorsLoading, setErrorsLoading] = useState(false);

  const loadErrors = async (hours) => {
    setErrorsLoading(true);
    try {
      const res = await api.get(`/stats/errors`, { params: { hours, limit: 200 } });
      setErrorsData(res.data);
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile caricare il dettaglio errori",
        variant: "destructive",
      });
    } finally {
      setErrorsLoading(false);
    }
  };

  useEffect(() => {
    if (errorsOpen) loadErrors(errorsHours);
  }, [errorsOpen, errorsHours]);

  const refresh = async () => {
    try {
      const res = await api.get(`/stats/overview`);
      setData(res.data);
    } catch (err) {
      toast({
        title: "Errore",
        description: err?.response?.data?.detail || "Impossibile caricare stats",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.uploads_7d.map((u, i) => ({
      day: u.day.slice(5),
      uploads: u.count,
      downloads: data.downloads_7d[i]?.count || 0,
    }));
  }, [data]);

  if (!isAdmin) {
    return (
      <Card data-testid="stats-not-admin" className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle>Permesso negato</CardTitle>
          <CardDescription className="text-white/60">Solo admin/super_admin può vedere le statistiche.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div data-testid="stats-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div data-testid="stats-title" className="text-2xl font-semibold tracking-tight">
            Stats
          </div>
          <div data-testid="stats-subtitle" className="text-sm text-white/60">
            Basate su metadata file + audit log.
          </div>
        </div>
        <Button
          data-testid="stats-refresh-button"
          onClick={refresh}
          variant="secondary"
          className="rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/10"
        >
          Aggiorna
        </Button>
      </div>

      {!data ? (
        <Card data-testid="stats-loading" className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle>Caricamento…</CardTitle>
          </CardHeader>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle data-testid="stats-files-total" className="text-base">Files attivi</CardTitle>
              <CardDescription className="text-white/60">Totale</CardDescription>
            </CardHeader>
            <CardContent>
              <div data-testid="stats-files-total-value" className="text-3xl font-semibold">
                {data.files_total}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle data-testid="stats-bytes-total" className="text-base">Storage (attivo)</CardTitle>
              <CardDescription className="text-white/60">Metadata vs. contenuto su disco</CardDescription>
            </CardHeader>
            <CardContent>
              <div data-testid="stats-bytes-total-value" className="text-3xl font-semibold">
                {formatBytes(data.bytes_total)}
              </div>
              <div data-testid="stats-bytes-on-disk" className="mt-1 text-sm text-white/60">
                Su disco: <span className="text-white/80">{formatBytes(data.bytes_on_disk)}</span>
              </div>
              {data.files_missing_blob > 0 || data.orphan_blobs > 0 || data.stale_upload_dirs > 0 ? (
                <div data-testid="stats-storage-drift" className="mt-3 space-y-1 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-200">
                  {data.files_missing_blob > 0 && (
                    <div data-testid="stats-missing-blobs">
                      {data.files_missing_blob} file attivi senza contenuto su disco ({formatBytes(data.bytes_missing)}): non sono scaricabili.
                    </div>
                  )}
                  {data.orphan_blobs > 0 && (
                    <div data-testid="stats-orphan-blobs">
                      {data.orphan_blobs} blob su disco senza record ({formatBytes(data.orphan_bytes)}): spazio non riferito da alcun file.
                    </div>
                  )}
                  {data.stale_upload_dirs > 0 && (
                    <div data-testid="stats-stale-uploads">
                      {data.stale_upload_dirs} upload interrotti mai ripuliti ({formatBytes(data.stale_upload_bytes)}).
                    </div>
                  )}
                </div>
              ) : (
                <div data-testid="stats-storage-ok" className="mt-3 rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-2 text-xs text-emerald-200">
                  Metadata e disco allineati.
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle data-testid="stats-errors-24h" className="text-base">Errori (24h)</CardTitle>
              <CardDescription className="text-white/60">Denied / error</CardDescription>
            </CardHeader>
            <CardContent>
              <div data-testid="stats-errors-24h-value" className="text-3xl font-semibold">
                {data.errors_24h}
              </div>
              <Button
                data-testid="stats-errors-detail-button"
                onClick={() => setErrorsOpen(true)}
                variant="secondary"
                size="sm"
                className="mt-3 rounded-lg bg-white/10 hover:bg-white/15 text-white border border-white/10"
                disabled={!data.errors_24h}
              >
                Vedi dettagli
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10 lg:col-span-3">
            <CardHeader>
              <CardTitle data-testid="stats-chart-title" className="text-base">Upload/Download ultimi 7 giorni</CardTitle>
              <CardDescription data-testid="stats-chart-desc" className="text-white/60">
                Visualizzazione rapida (MVP).
              </CardDescription>
            </CardHeader>
            <CardContent className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barGap={6} barSize={14}>
                  <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                  <XAxis dataKey="day" stroke="rgba(255,255,255,0.5)" tickLine={false} axisLine={false} />
                  <YAxis stroke="rgba(255,255,255,0.5)" tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{
                      background: "rgba(10, 14, 28, 0.95)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      borderRadius: 12,
                      color: "white",
                    }}
                  />
                  <Bar dataKey="uploads" fill="rgba(99,102,241,0.55)" radius={[10, 10, 0, 0]} />
                  <Bar dataKey="downloads" fill="rgba(217,70,239,0.35)" radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog open={errorsOpen} onOpenChange={setErrorsOpen}>
        <DialogContent data-testid="stats-errors-dialog" className="bg-[#0b1020] border-white/10 text-white max-w-5xl">
          <DialogHeader>
            <DialogTitle data-testid="stats-errors-dialog-title">Errori: denied / error</DialogTitle>
            <DialogDescription className="text-white/60">
              Eventi di audit con esito negato o errore. Il conteggio della card copre le ultime 24 ore.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2">
            {[24, 72, 168].map((h) => (
              <Button
                key={h}
                data-testid={`stats-errors-range-${h}`}
                onClick={() => setErrorsHours(h)}
                size="sm"
                variant="secondary"
                className={`rounded-lg border text-white ${
                  errorsHours === h ? "bg-white/20 border-white/25" : "bg-white/5 hover:bg-white/10 border-white/10"
                }`}
              >
                {h === 24 ? "24 ore" : h === 72 ? "3 giorni" : "7 giorni"}
              </Button>
            ))}
            {errorsData && (
              <div data-testid="stats-errors-total" className="ml-auto text-sm text-white/60">
                {errorsData.total} eventi
                {errorsData.returned < errorsData.total && ` — mostrati i ${errorsData.returned} più recenti`}
              </div>
            )}
          </div>

          {errorsLoading || !errorsData ? (
            <div data-testid="stats-errors-loading" className="py-8 text-center text-white/60">Caricamento…</div>
          ) : errorsData.total === 0 ? (
            <div data-testid="stats-errors-empty" className="py-8 text-center text-white/60">Nessun errore nel periodo.</div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="mb-2 text-sm font-medium text-white/80">Raggruppati per causa</div>
                <ScrollArea type="auto" className="max-h-[180px] w-full pb-2">
                  <div className="min-w-[620px]">
                    <Table data-testid="stats-errors-summary-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-white/60">Azione</TableHead>
                          <TableHead className="text-white/60">Esito</TableHead>
                          <TableHead className="text-white/60 w-[70px]">HTTP</TableHead>
                          <TableHead className="text-white/60">Dettaglio</TableHead>
                          <TableHead className="text-white/60 text-right w-[70px]">Occorr.</TableHead>
                          <TableHead className="text-white/60 whitespace-nowrap">Ultimo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {errorsData.summary.map((s, i) => (
                          <TableRow key={i} data-testid={`stats-errors-summary-row-${i}`}>
                            <TableCell className="text-sm text-white/80">{s.action}</TableCell>
                            <TableCell>
                              <Badge className={`border ${s.result === "error" ? "bg-red-500/10 border-red-500/20 text-red-200" : "bg-amber-500/10 border-amber-500/20 text-amber-200"}`}>
                                {s.result}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-white/70">{s.status ?? "—"}</TableCell>
                            <TableCell className="text-sm text-white/70">{s.detail ?? "—"}</TableCell>
                            <TableCell className="text-sm text-white/90 text-right font-medium">{s.count}</TableCell>
                            <TableCell className="text-sm text-white/60 whitespace-nowrap">{formatDateTimeShort(s.last_seen)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </ScrollArea>
              </div>

              <div>
                <div className="mb-2 text-sm font-medium text-white/80">Eventi</div>
                <ScrollArea type="auto" className="max-h-[320px] w-full pb-2">
                  <div className="min-w-[980px]">
                    <Table data-testid="stats-errors-items-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-white/60 whitespace-nowrap">Quando</TableHead>
                          <TableHead className="text-white/60">Azione</TableHead>
                          <TableHead className="text-white/60 w-[70px]">HTTP</TableHead>
                          <TableHead className="text-white/60">Dettaglio</TableHead>
                          <TableHead className="text-white/60">Percorso</TableHead>
                          <TableHead className="text-white/60">Attore</TableHead>
                          <TableHead className="text-white/60">Tenant</TableHead>
                          <TableHead className="text-white/60">IP</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {errorsData.items.map((e, i) => (
                          <TableRow key={e.correlation_id || i} data-testid={`stats-errors-row-${i}`}>
                            <TableCell className="text-sm text-white/70 whitespace-nowrap">{formatDateTimeShort(e.timestamp)}</TableCell>
                            <TableCell className="text-sm text-white/80">
                              {e.action}
                              <div className="text-xs text-white/40">{e.result}</div>
                            </TableCell>
                            <TableCell className="text-sm text-white/70">{e.status ?? "—"}</TableCell>
                            <TableCell className="text-sm text-white/70 max-w-[220px] truncate" title={e.detail || ""}>{e.detail ?? "—"}</TableCell>
                            <TableCell className="text-sm text-white/60 max-w-[220px] truncate" title={`${e.method || ""} ${e.path || ""}`}>
                              {e.path ? `${e.method || ""} ${e.path}`.trim() : "—"}
                            </TableCell>
                            <TableCell className="text-sm text-white/60 max-w-[150px] truncate" title={e.actor_id || ""}>
                              {e.actor_type || "—"}
                              {e.actor_id && e.actor_id !== "-" && <div className="text-xs text-white/40 truncate">{e.actor_id}</div>}
                            </TableCell>
                            <TableCell className="text-sm text-white/60 max-w-[140px] truncate" title={e.tenant_name || e.tenant_id || ""}>
                              {e.tenant_name || e.tenant_id || "—"}
                            </TableCell>
                            <TableCell className="text-sm text-white/60 whitespace-nowrap">{e.ip || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </ScrollArea>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RootApp() {
  const { user, loading, refresh } = useAuth();
  const { toast } = useToast();
  const location = useLocation();

  useEffect(() => {
    document.title = APP_NAME;
  }, [location.pathname]);

  const doLogout = async () => {
    try {
      await api.post(`/auth/logout`);
    } catch {
      // ignore
    }
    await refresh();
    toast({ title: "Logout" });
  };

  const isSuperAdmin = user?.role === "super_admin";
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const canUpload = ["super_admin", "admin", "user"].includes(user?.role);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage onLogin={refresh} />} />

      <Route
        path="/*"
        element={
          <Protected user={user} loading={loading}>
            <AppShell user={user} onLogout={doLogout}>
              <Routes>
                <Route path="/" element={<Navigate to="/files" replace />} />
                <Route path="/files" element={<FilesPage isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} canUpload={canUpload} />} />
                <Route path="/stats" element={<StatsPage isAdmin={isAdmin} />} />
                <Route path="/audit" element={<AuditPage isAdmin={isAdmin} />} />
                <Route path="/tenants" element={<TenantsKeysPage isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />} />
                <Route path="/users" element={<UsersPage user={user} />} />
                <Route
                  path="*"
                  element={
                    <div data-testid="not-found" className="p-8 text-white/70">
                      Pagina non trovata.
                    </div>
                  }
                />
              </Routes>
            </AppShell>
          </Protected>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <RootApp />
    </BrowserRouter>
  );
}
